import type { File as FileType } from '@kalos/kalos-rpc';
import { QuoteDocument } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  LoadingIcon,
  toast,
} from '@kalos/ui';
import {
  CheckCircledIcon,
  DownloadIcon,
  ExclamationTriangleIcon,
  FileIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { format, parseISO } from 'date-fns';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useUpdateQuoteDocumentMutation } from '../../../hooks/react-query/useQuoteDocumentQuery';
import { ImageGallery } from '../../ComponentsLibrary/OmniGallery/components/ImageGallery';
import { isProposalExpired, isValidDate } from '../proposalExpiryUtils';
import {
  fetchProposalPdfBlob,
  resolveSignedContractGalleryFile,
} from './proposalDocumentPreviewHelpers';

interface ProposalStatusProps {
  customer: {
    name: string;
    email: string;
  };
  proposalId: string;
  sentTimestamp: string;
  signedTimestamp?: string;
  quoteDocument?: QuoteDocument | null;
  jobStatus?: string;
  generateLinkButton?: ReactNode;
  onQuickUpdate?: () => void;
  hasApprovedLineItems?: boolean;
}

type PdfPreviewState = {
  open: boolean;
  loading: boolean;
  title: string;
  /** S3 file row — drives ImageGallery (same path as transaction gallery PDFs). */
  galleryFile: FileType | null;
  /** Generated proposal PDF — blob iframe preview (no S3 File row). */
  blobUrl: string | null;
  blob: Blob | null;
  downloadFileName: string;
};

const initialPdfPreview: PdfPreviewState = {
  open: false,
  loading: false,
  title: '',
  galleryFile: null,
  blobUrl: null,
  blob: null,
  downloadFileName: '',
};

export const ProposalStatus = ({
  customer,
  proposalId,
  sentTimestamp,
  signedTimestamp,
  quoteDocument,
  jobStatus,
  generateLinkButton,
  onQuickUpdate,
  hasApprovedLineItems = false,
}: ProposalStatusProps) => {
  const [pdfPreview, setPdfPreview] = useState<PdfPreviewState>(initialPdfPreview);

  const expired = isProposalExpired(quoteDocument, jobStatus);

  const closePdfPreview = useCallback(() => {
    setPdfPreview((prev) => {
      if (prev.blobUrl) {
        window.URL.revokeObjectURL(prev.blobUrl);
      }
      return initialPdfPreview;
    });
  }, []);

  const triggerBlobDownload = useCallback(() => {
    const { blob, blobUrl, downloadFileName } = pdfPreview;
    let url: string;
    if (blob) {
      url = window.URL.createObjectURL(blob);
    } else if (blobUrl) {
      url = blobUrl;
    } else {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (blob) {
      window.URL.revokeObjectURL(url);
    }
  }, [pdfPreview]);

  const handleOpenProposalPdfPreview = async () => {
    if (!quoteDocument?.id) return;

    setPdfPreview({
      ...initialPdfPreview,
      open: true,
      loading: true,
      title: 'Proposal PDF',
      downloadFileName: `proposal-${proposalId}.pdf`,
    });

    try {
      const blob = await fetchProposalPdfBlob(parseInt(proposalId, 10), quoteDocument.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setPdfPreview((prev) => ({
        ...prev,
        loading: false,
        blobUrl: url,
        blob,
      }));
    } catch (error) {
      console.error('Proposal PDF preview error:', error);
      toast({
        title: 'Could not load proposal PDF',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      closePdfPreview();
    }
  };

  const handleOpenSignedPdfPreview = async () => {
    if (!quoteDocument?.documentId) return;

    setPdfPreview({
      ...initialPdfPreview,
      open: true,
      loading: true,
      title: 'Signed contract',
      downloadFileName: '',
    });

    try {
      const galleryFile = await resolveSignedContractGalleryFile(quoteDocument);
      if (!galleryFile) {
        throw new Error('Signed contract file is not available');
      }

      setPdfPreview((prev) => ({
        ...prev,
        loading: false,
        galleryFile,
        downloadFileName: galleryFile.name,
      }));
    } catch (error) {
      console.error('Signed PDF preview error:', error);
      toast({
        title: 'Could not load signed contract',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      closePdfPreview();
    }
  };

  if (expired && quoteDocument) {
    return (
      <ExpiredProposal
        customer={customer}
        proposalId={proposalId}
        sentTimestamp={sentTimestamp}
        quoteDocument={quoteDocument}
      />
    );
  }

  return (
    <div className="mb-4 rounded-md bg-[#e6f7ed] p-4">
      {/* Success alert */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <CheckCircledIcon className="h-5 w-5 flex-shrink-0 text-green-600" />
          <div>
            <div className="mb-1 font-bold font-medium text-[#027948]">
              Proposal Delivered Successfully!
            </div>
            <div className="text-sm text-[#027948]">
              Proposal {proposalId} shared with {customer.name?.trim() || 'N/A'}
            </div>
            <div className="text-sm text-[#027948]">
              Method:{' '}
              {quoteDocument?.inPerson == 1 ? 'In-Person on Device' : `Email to ${customer.email}`}
            </div>
            <div className="text-sm text-[#027948]">
              Delivered: {format(parseISO(sentTimestamp), 'M/d/yyyy h:mm a')}
            </div>
          </div>
        </div>
      </div>

      {/* Downloads and Customer Info */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="mb-2 font-medium">Downloads</div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleOpenProposalPdfPreview}
              disabled={pdfPreview.open && pdfPreview.loading}
              className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileIcon className="h-4 w-4" />
              {pdfPreview.open &&
              pdfPreview.loading &&
              pdfPreview.title === 'Proposal PDF' &&
              !pdfPreview.galleryFile &&
              !pdfPreview.blobUrl
                ? 'Opening…'
                : 'View proposal PDF'}
            </button>
            {signedTimestamp && (
              <>
                <div className="rounded-md bg-white px-3 py-2 text-sm text-[#027948]">
                  <div className="flex items-center gap-2">
                    <CheckCircledIcon className="h-4 w-4" />
                    Customer Signed!
                  </div>
                  <div className="mt-1">
                    Signed on {format(parseISO(signedTimestamp), 'M/d/yyyy h:mm a')}
                  </div>
                </div>
                {quoteDocument?.documentId && (
                  <button
                    type="button"
                    onClick={handleOpenSignedPdfPreview}
                    disabled={pdfPreview.open && pdfPreview.loading}
                    className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FileIcon className="h-4 w-4" />
                    {pdfPreview.open && pdfPreview.loading && pdfPreview.title === 'Signed contract'
                      ? 'Opening…'
                      : 'View signed contract'}
                  </button>
                )}
              </>
            )}

            {/* Proposal Quick Update Button - Shown when signed OR has approved line items */}
            {(signedTimestamp || hasApprovedLineItems) && onQuickUpdate && (
              <button
                onClick={onQuickUpdate}
                className="bg-primary hover:bg-primary/90 w-full rounded-md px-3 py-2 text-sm font-medium text-white"
              >
                Proposal Quick Update
              </button>
            )}

            {/* Generate Link Button */}
            {generateLinkButton && <div className="pt-2">{generateLinkButton}</div>}
          </div>
        </div>

        <div>
          <div className="mb-2 font-medium">Customer</div>
          <div className="space-y-1 text-sm">
            <div>Name: {customer.name?.trim() || 'N/A'}</div>
            <div>Email: {customer.email}</div>
          </div>
        </div>
      </div>

      {/* Warning box */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <InfoCircledIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" aria-hidden />
          <div>
            <p className="text-sm text-blue-700">
              Only make changes if you intend to send a new version to the customer.
              <br />
              Note: Any changes you make to this proposal will be automatically reflected in the
              customers digital experience.
              <br />
              However, the downloadable PDF and official version are only updated when you send the
              proposal again.
            </p>
          </div>
        </div>
      </div>

      <Dialog
        open={pdfPreview.open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closePdfPreview();
        }}
      >
        <DialogContent className="fixed inset-0 left-0 top-0 z-50 flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 pt-10 sm:max-w-none sm:pt-10">
          <DialogHeader className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="text-left">{pdfPreview.title}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {pdfPreview.loading ? (
              <div className="flex flex-1 items-center justify-center py-16">
                <LoadingIcon className="text-muted-foreground h-10 w-10 animate-spin" />
              </div>
            ) : pdfPreview.galleryFile ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2">
                <ImageGallery files={[pdfPreview.galleryFile]} title="" />
              </div>
            ) : pdfPreview.blobUrl ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-hidden px-2">
                  <iframe
                    title="pdf-preview"
                    src={pdfPreview.blobUrl}
                    className="h-full min-h-0 w-full flex-1"
                    style={{ border: 'none' }}
                  />
                </div>
                <div className="flex flex-shrink-0 justify-center border-t border-gray-200 p-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={triggerBlobDownload}
                    className="flex items-center gap-1"
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface ExpiredProposalProps {
  customer: {
    name: string;
    email: string;
  };
  proposalId: string;
  sentTimestamp: string;
  quoteDocument: QuoteDocument;
}

function ExpiredProposal({
  customer,
  proposalId,
  sentTimestamp,
  quoteDocument,
}: ExpiredProposalProps) {
  const [notes, setNotes] = useState(quoteDocument.lostReason ?? '');
  const updateMutation = useUpdateQuoteDocumentMutation();

  // Sync local state if the underlying record changes (e.g. after a save refetch)
  useEffect(() => {
    setNotes(quoteDocument.lostReason ?? '');
  }, [quoteDocument.lostReason]);

  const method = quoteDocument.inPerson == 1 ? 'In-Person on Device' : `Email to ${customer.email}`;

  const handleSave = () => {
    const payload = QuoteDocument.create({
      id: quoteDocument.id,
      eventId: quoteDocument.eventId,
      lostReason: notes,
      fieldMask: ['LostReason'],
    });
    updateMutation.mutate(payload);
  };

  const hasUnsavedChanges = notes !== (quoteDocument.lostReason ?? '');

  return (
    <div className="mb-4 space-y-4">
      <div className="rounded-md border border-red-200 bg-[#fdecea] p-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-700" />
          <div>
            <div className="mb-1 font-bold font-medium text-red-800">Proposal expired</div>
            <div className="text-sm text-red-800">
              Proposal {proposalId} shared with {customer.name?.trim() || 'N/A'}
            </div>
            <div className="text-sm text-red-800">Method: {method}</div>
            <div className="text-sm text-red-800">
              Delivered: {format(parseISO(sentTimestamp), 'M/d/yyyy')}
              {isValidDate(quoteDocument.quoteExpireDate) && (
                <>
                  {' · '}
                  Expired: {format(parseISO(quoteDocument.quoteExpireDate), 'M/d/yyyy')}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-gray-200 bg-white p-4">
        <label
          htmlFor="expired-proposal-notes"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Notes (optional)
        </label>
        <textarea
          id="expired-proposal-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any context from follow-up call..."
          className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending || !hasUnsavedChanges}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
